import React, { forwardRef, useState } from "react";
import ProductTypePills from "../ProductTypePills/ProductTypePills";
import useLicenseSearch from "./useLicenseSearch";
import {
    PARTNER_NAME_MAX,
    PARTNER_TYPES,
    PROFILE_TO_PARTNER_TYPE,
    SUPPLY_CHAIN_COPY,
    US_STATES,
    partnerTypeLabel,
} from "../../constants/supplyChain";

const COPY = SUPPLY_CHAIN_COPY.entry;

/**
 * One supply-chain partner (a vendor or customer). Three states:
 *   search  — fresh row: search DYC, or switch to adding the business by name
 *   linked  — a DYC business: identity and type come from its profile (locked)
 *   manual  — added by name: name + type required, everything else optional
 * Contact details and product types can be added in every state.
 *
 * `errors` is the editor's errors object; this row reads `${row.key}.<field>`.
 * The forwarded ref goes to the row's first focusable input (focus on add).
 */
const PartnerEntry = forwardRef(function PartnerEntry(
    { row, index, itemLabel, onChange, onRemove, errors = {}, searchMode, excludeLicenseNo, disabled = false },
    ref
) {
    const [showDetails, setShowDetails] = useState(
        Boolean(row.city || row.state || row.usdaLicenseNumber || row.ein || row.contactName || row.contactEmail || row.contactPhone)
    );
    const search = useLicenseSearch({ mode: searchMode, excludeLicenseNo });
    const baseId = `partner-${row.key}`;
    const err = (field) => errors[`${row.key}.${field}`];
    const set = (patch) => onChange({ ...row, ...patch });

    const isLinked = Boolean(row.linkedLicenseId);
    const isManual = !isLinked && (row.manual || Boolean(row.partnerName));
    const isSearching = !isLinked && !isManual;

    const pick = (license) => {
        search.reset();
        set({
            linkedLicenseId: license.id,
            linkedProfile: license.profile,
            onDyc: true,
            manual: false,
            partnerName: license.company,
            partnerType: PROFILE_TO_PARTNER_TYPE[license.profile] || "",
            city: license.city || "",
            state: license.state || "",
            usdaLicenseNumber: "",
            ein: "",
        });
    };
    const startManual = () => {
        set({ manual: true, partnerName: search.query.trim().slice(0, PARTNER_NAME_MAX) });
        search.reset();
    };
    const backToSearch = () =>
        set({ linkedLicenseId: null, linkedProfile: null, onDyc: false, manual: false, partnerName: "", partnerType: "" });

    const field = (name, label, props = {}) => {
        const id = `${baseId}-${name}`;
        const error = err(name);
        return (
            <div className="scp-field">
                <label htmlFor={id} className="scp-label">
                    {label}
                </label>
                <input
                    id={id}
                    className={`scp-input${error ? " scp-input--error" : ""}`}
                    value={row[name] || ""}
                    onChange={(e) => set({ [name]: e.target.value })}
                    disabled={disabled}
                    aria-invalid={error ? "true" : undefined}
                    aria-describedby={error ? `${id}-error` : undefined}
                    autoComplete="off"
                    {...props}
                />
                {error && (
                    <span id={`${id}-error`} role="alert" className="scp-error">
                        {error}
                    </span>
                )}
            </div>
        );
    };

    const titleId = `${baseId}-title`;
    return (
        <div className="scp-entry" role="group" aria-labelledby={titleId}>
            <div className="scp-entry-header">
                <span id={titleId} className="scp-entry-title">
                    {`${itemLabel[0].toUpperCase()}${itemLabel.slice(1)} ${index + 1}`}
                </span>
                <button
                    type="button"
                    className="scp-link scp-remove"
                    onClick={onRemove}
                    disabled={disabled}
                    aria-label={`${COPY.remove} ${itemLabel} ${index + 1}`}
                >
                    {COPY.remove}
                </button>
            </div>

            {row.disputed && <p className="scp-disputed">{COPY.disputed}</p>}

            {isSearching && (
                <div className="scp-search">
                    <label htmlFor={`${baseId}-search`} className="scp-label">
                        {COPY.searchLabel}
                    </label>
                    <input
                        id={`${baseId}-search`}
                        ref={ref}
                        className={`scp-input${err("partnerName") ? " scp-input--error" : ""}`}
                        placeholder={COPY.searchPlaceholder}
                        value={search.query}
                        onChange={(e) => search.search(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                        disabled={disabled}
                        autoComplete="off"
                        aria-describedby={`${baseId}-search-status`}
                    />
                    <p id={`${baseId}-search-status`} className="scp-hint" aria-live="polite">
                        {search.status === "searching" && COPY.searching}
                        {search.status === "done" && search.results.length === 0 && COPY.noResults}
                    </p>
                    {search.results.length > 0 && (
                        <ul className="scp-results" aria-label={`${COPY.searchLabel} results`}>
                            {search.results.map((r) => (
                                <li key={r.id}>
                                    <button type="button" className="scp-result" onClick={() => pick(r)} disabled={disabled}>
                                        <strong>{r.company}</strong>
                                        <span>
                                            {[partnerTypeLabel(PROFILE_TO_PARTNER_TYPE[r.profile]), [r.city, r.state].filter(Boolean).join(", ")]
                                                .filter(Boolean)
                                                .join(" · ")}
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                    <button type="button" className="scp-link" onClick={startManual} disabled={disabled}>
                        {COPY.addManually}
                    </button>
                </div>
            )}

            {isLinked && (
                <div className="scp-linked">
                    <span className="scp-badge scp-badge--dyc">✓ {COPY.onDyc}</span>
                    <div className="scp-linked-name">{row.partnerName}</div>
                    <div className="scp-hint">
                        {[partnerTypeLabel(row.partnerType), [row.city, row.state].filter(Boolean).join(", ")].filter(Boolean).join(" · ")}
                        {row.partnerType && <span className="scp-sr-only"> ({COPY.typeLockedHint})</span>}
                    </div>
                    <button type="button" className="scp-link" onClick={backToSearch} disabled={disabled} ref={ref}>
                        {COPY.changeBusiness}
                    </button>
                </div>
            )}

            {isManual && (
                <div className="scp-manual">
                    <div className="scp-row">
                        <div className="scp-field scp-field--grow">
                            <label htmlFor={`${baseId}-partnerName`} className="scp-label scp-required">
                                {COPY.nameLabel}
                            </label>
                            <input
                                id={`${baseId}-partnerName`}
                                ref={ref}
                                className={`scp-input${err("partnerName") ? " scp-input--error" : ""}`}
                                value={row.partnerName}
                                maxLength={PARTNER_NAME_MAX}
                                onChange={(e) => set({ partnerName: e.target.value })}
                                onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                                disabled={disabled}
                                aria-required="true"
                                aria-invalid={err("partnerName") ? "true" : undefined}
                                aria-describedby={err("partnerName") ? `${baseId}-partnerName-error` : undefined}
                                autoComplete="off"
                            />
                            {err("partnerName") && (
                                <span id={`${baseId}-partnerName-error`} role="alert" className="scp-error">
                                    {err("partnerName")}
                                </span>
                            )}
                        </div>
                        <div className="scp-field">
                            <label htmlFor={`${baseId}-partnerType`} className="scp-label scp-required">
                                {COPY.typeLabel}
                            </label>
                            <select
                                id={`${baseId}-partnerType`}
                                className={`scp-input${err("partnerType") ? " scp-input--error" : ""}`}
                                value={row.partnerType || ""}
                                onChange={(e) => set({ partnerType: e.target.value })}
                                disabled={disabled}
                                aria-required="true"
                                aria-invalid={err("partnerType") ? "true" : undefined}
                                aria-describedby={err("partnerType") ? `${baseId}-partnerType-error` : undefined}
                            >
                                <option value="">{COPY.typePlaceholder}</option>
                                {PARTNER_TYPES.map((t) => (
                                    <option key={t.value} value={t.value}>
                                        {t.label}
                                    </option>
                                ))}
                            </select>
                            {err("partnerType") && (
                                <span id={`${baseId}-partnerType-error`} role="alert" className="scp-error">
                                    {err("partnerType")}
                                </span>
                            )}
                        </div>
                    </div>
                    {!row.id && (
                        <button type="button" className="scp-link" onClick={backToSearch} disabled={disabled}>
                            {COPY.backToSearch}
                        </button>
                    )}
                </div>
            )}

            {err("linked") && (
                <span role="alert" className="scp-error">
                    {err("linked")}
                </span>
            )}

            {!isSearching && (
                <>
                    <div className="scp-field">
                        <span className="scp-label" id={`${baseId}-products`}>
                            {COPY.productTypesLabel}
                        </span>
                        <div role="group" aria-labelledby={`${baseId}-products`}>
                            <ProductTypePills
                                selected={row.productTypes || []}
                                disabled={disabled}
                                onToggle={(t) =>
                                    set({
                                        productTypes: row.productTypes.includes(t)
                                            ? row.productTypes.filter((x) => x !== t)
                                            : [...row.productTypes, t],
                                    })
                                }
                            />
                        </div>
                    </div>

                    <button
                        type="button"
                        className="scp-link"
                        aria-expanded={showDetails}
                        aria-controls={`${baseId}-details`}
                        onClick={() => setShowDetails((v) => !v)}
                    >
                        {showDetails ? "− " : "+ "}
                        {COPY.moreDetails}
                    </button>
                    {showDetails && (
                        <div id={`${baseId}-details`} className="scp-details">
                            <p className="scp-hint">{COPY.moreDetailsHint}</p>
                            {isManual && (
                                <div className="scp-row">
                                    {field("city", COPY.cityLabel, { maxLength: 100 })}
                                    <div className="scp-field">
                                        <label htmlFor={`${baseId}-state`} className="scp-label">
                                            {COPY.stateLabel}
                                        </label>
                                        <select
                                            id={`${baseId}-state`}
                                            className="scp-input"
                                            value={row.state || ""}
                                            onChange={(e) => set({ state: e.target.value })}
                                            disabled={disabled}
                                        >
                                            <option value="">—</option>
                                            {US_STATES.map((s) => (
                                                <option key={s} value={s}>
                                                    {s}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            )}
                            {isManual && (
                                <div className="scp-row">
                                    {field("usdaLicenseNumber", COPY.usdaLabel, { maxLength: 50 })}
                                    {field("ein", COPY.einLabel, { maxLength: 20, inputMode: "numeric" })}
                                </div>
                            )}
                            <div className="scp-row">
                                {field("contactName", COPY.contactNameLabel, { maxLength: 200 })}
                                {field("contactEmail", COPY.contactEmailLabel, { type: "email", maxLength: 255 })}
                                {field("contactPhone", COPY.contactPhoneLabel, { type: "tel", maxLength: 50 })}
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
});

export default PartnerEntry;
